import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import moment from "moment";
import translate from "../../i18n/translate";
import PieChartIcon from "@mui/icons-material/PieChart";
import BarChartIcon from "@mui/icons-material/BarChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import TimelineIcon from "@mui/icons-material/Timeline";

const COLORS = [
  "#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8",
  "#82CA9D", "#FFC658", "#FF6B9D", "#C56CF0", "#17C0EB",
  "#7158E2", "#3AE374", "#FF3838", "#FF9F1A", "#01A3A4"
];

const StatisticsModal = ({ open, onClose, reportData, dateRange }) => {
  const [chartType, setChartType] = useState("pie");

  // Calculate statistics - uses ALL reportData from parent (already filtered by report's date range)
  const statistics = useMemo(() => {
    if (!reportData || reportData.length === 0) {
      return {
        billerData: [],
        timelineData: [],
        billerNames: [],
        totalSales: 0,
        totalTransactions: 0,
      };
    }

    console.log('📊 Calculating statistics from report data...');
    console.log('📊 Report data length:', reportData.length);

    // Use all report data - no additional filtering needed
    const filteredData = reportData;

    console.log('📊 Processing data count:', filteredData.length);

    const billerStats = {};
    const dateByBillerStats = {};
    const allDates = new Set();
    const allBillers = new Set();
    let totalSales = 0;
    let totalTransactions = 0;

    // Process each transaction
    filteredData.forEach((item) => {
      // Extract biller name
      const biller = item.provider || item.company || item.biller || "Other";
      
      // Extract amount from dealercost (negative, so take absolute value)
      const dealercost = item.dealercost || 0;
      const amount = Math.abs(parseFloat(dealercost) || 0);
      
      // Check if successful
      const statusStr = String(item.status || "").toLowerCase();
      const isSuccess = statusStr === "success" || statusStr === "completed" || statusStr === "1";
      
      if (!isSuccess) return;

      // Format date
      const itemDate = moment(item.datetime || item.date).format('YYYY-MM-DD');
      
      allDates.add(itemDate);
      allBillers.add(biller);

      // Aggregate by biller
      if (!billerStats[biller]) {
        billerStats[biller] = { name: biller, value: 0, count: 0 };
      }
      billerStats[biller].value += amount;
      billerStats[biller].count += 1;

      // Aggregate by date and biller
      if (!dateByBillerStats[itemDate]) {
        dateByBillerStats[itemDate] = {};
      }
      if (!dateByBillerStats[itemDate][biller]) {
        dateByBillerStats[itemDate][biller] = 0;
      }
      dateByBillerStats[itemDate][biller] += amount;

      totalSales += amount;
      totalTransactions += 1;
    });

    console.log('📊 Total sales:', totalSales);
    console.log('📊 Unique dates found:', Array.from(allDates));
    console.log('📊 Unique billers found:', Array.from(allBillers));

    // Convert to arrays
    const billerData = Object.values(billerStats)
      .sort((a, b) => b.value - a.value)
      .map((item) => ({
        name: item.name,
        value: parseFloat(item.value.toFixed(2)),
        count: item.count,
        percentage: totalSales > 0 ? ((item.value / totalSales) * 100).toFixed(1) : 0,
      }));

    const sortedDates = Array.from(allDates).sort();
    const billerNames = Array.from(allBillers);

    const timelineData = sortedDates.map((date) => {
      const entry = {
        date: moment(date).format('MMM DD'),
        fullDate: date,
        Total: 0, // Initialize total for this date
      };

      // Add each biller's amount and accumulate total
      billerNames.forEach((biller) => {
        const billerAmount = dateByBillerStats[date]?.[biller] 
          ? parseFloat(dateByBillerStats[date][biller].toFixed(2))
          : 0;
        entry[biller] = billerAmount;
        entry.Total += billerAmount;
      });

      // Round the total
      entry.Total = parseFloat(entry.Total.toFixed(2));

      return entry;
    });

    console.log('📊 Timeline data generated:', timelineData);

    return {
      billerData,
      timelineData,
      billerNames,
      totalSales: totalSales.toFixed(2),
      totalTransactions,
    };
  }, [reportData]);

  const handleChartTypeChange = (event, newType) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };

  const renderChart = () => {
    const { billerData, timelineData, billerNames } = statistics;

    if (statistics.totalTransactions === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="textSecondary">
            No data available for the selected date range
          </Typography>
        </Box>
      );
    }

    switch (chartType) {
      case "pie":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={billerData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label={(entry) => `${entry.name}: ₪${entry.value}`}
              >
                {billerData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `₪${parseFloat(value).toLocaleString()}`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );

      case "bar":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={billerData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip formatter={(value) => `₪${parseFloat(value).toLocaleString()}`} />
              <Legend />
              <Bar dataKey="value" name="Sales Amount" fill="#8884d8">
                {billerData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
              <Bar dataKey="count" name="Transaction Count" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        );

      case "line":
        return (
          <ResponsiveContainer width="100%" height={500}>
            <LineChart data={timelineData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                angle={-45} 
                textAnchor="end" 
                height={80}
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                label={{ value: 'Amount (₪)', angle: -90, position: 'insideLeft' }}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value) => value ? `₪${parseFloat(value).toLocaleString()}` : '₪0'}
                contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #ccc' }}
              />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="line"
              />
              {/* Total sales line - bold and distinctive */}
              <Line
                key="Total"
                type="monotone"
                dataKey="Total"
                name="Total Sales"
                stroke="#000000"
                strokeWidth={3.5}
                strokeDasharray="5 5"
                dot={{ r: 6, fill: "#000000", strokeWidth: 2, stroke: "#fff" }}
                activeDot={{ r: 8 }}
                connectNulls={true}
              />
              {/* Individual biller lines */}
              {billerNames.map((biller, index) => (
                <Line
                  key={biller}
                  type="monotone"
                  dataKey={biller}
                  name={biller}
                  stroke={COLORS[index % COLORS.length]}
                  strokeWidth={2.5}
                  dot={{ r: 5, fill: COLORS[index % COLORS.length] }}
                  activeDot={{ r: 7 }}
                  connectNulls={true}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case "area":
        return (
          <ResponsiveContainer width="100%" height={500}>
            <AreaChart data={timelineData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                angle={-45} 
                textAnchor="end" 
                height={80}
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                label={{ value: 'Amount (₪)', angle: -90, position: 'insideLeft' }}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value) => value ? `₪${parseFloat(value).toLocaleString()}` : '₪0'}
                contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #ccc' }}
              />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
              />
              {/* Total sales area - overlay on top */}
              <Area
                key="Total"
                type="monotone"
                dataKey="Total"
                name="Total Sales"
                stroke="#000000"
                strokeWidth={3}
                fill="#000000"
                fillOpacity={0.1}
              />
              {/* Individual biller areas - stacked */}
              {billerNames.map((biller, index) => (
                <Area
                  key={biller}
                  type="monotone"
                  dataKey={biller}
                  name={biller}
                  stackId="1"
                  stroke={COLORS[index % COLORS.length]}
                  fill={COLORS[index % COLORS.length]}
                  fillOpacity={0.6}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">{translate("Sales Statistics")}</Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" color="primary">
              {translate("Total")}: ₪{parseFloat(statistics.totalSales).toLocaleString()}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              ({statistics.totalTransactions} {translate("transactions")})
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Chart Type Selector */}
        <Box display="flex" justifyContent="center" sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={chartType}
            exclusive
            onChange={handleChartTypeChange}
            aria-label="chart type"
            size="large"
          >
            <ToggleButton value="pie" aria-label="pie chart">
              <PieChartIcon sx={{ mr: 1 }} />
              {translate("Pie Chart")}
            </ToggleButton>
            <ToggleButton value="bar" aria-label="bar chart">
              <BarChartIcon sx={{ mr: 1 }} />
              {translate("Bar Chart")}
            </ToggleButton>
            <ToggleButton value="line" aria-label="line chart">
              <ShowChartIcon sx={{ mr: 1 }} />
              {translate("Line Chart")}
            </ToggleButton>
            <ToggleButton value="area" aria-label="area chart">
              <TimelineIcon sx={{ mr: 1 }} />
              {translate("Area Chart")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Chart Display */}
        <Box sx={{ mt: 3, mb: 3 }}>
          {renderChart()}
        </Box>

        {/* Statistics Table */}
        {statistics.billerData.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              {translate("Detailed Statistics")}
            </Typography>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "16px" }}>
              <thead>
                <tr style={{ backgroundColor: "#f5f5f5", borderBottom: "2px solid #ddd" }}>
                  <th style={{ padding: "12px", textAlign: "left" }}>{translate("Biller")}</th>
                  <th style={{ padding: "12px", textAlign: "right" }}>{translate("Transactions")}</th>
                  <th style={{ padding: "12px", textAlign: "right" }}>{translate("Amount")}</th>
                  <th style={{ padding: "12px", textAlign: "right" }}>{translate("Percentage")}</th>
                </tr>
              </thead>
              <tbody>
                {statistics.billerData.map((item, index) => (
                  <tr key={index} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={{ padding: "12px" }}>{item.name}</td>
                    <td style={{ padding: "12px", textAlign: "right" }}>{item.count}</td>
                    <td style={{ padding: "12px", textAlign: "right" }}>₪{item.value.toLocaleString()}</td>
                    <td style={{ padding: "12px", textAlign: "right" }}>{item.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          {translate("Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatisticsModal;
